import { useCallback, useEffect, useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { FolderCog, LayoutGrid, LogOut, Plus, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/api/client";
import AppCard from "@/components/AppCard";
import AppFormModal from "@/components/AppFormModal";
import CategoryManagerModal from "@/components/CategoryManagerModal";
import SearchBar from "@/components/SearchBar";
import { useAuth } from "@/lib/auth";

/** Apps with no category live in this pseudo-group, which always sorts last. */
const UNCATEGORIZED = "__uncategorized__";
const keyToCategoryId = (key) => (key === UNCATEGORIZED ? null : key);

function CardSkeleton() {
  return (
    <div className="flex w-[104px] flex-col items-center gap-2.5 px-2 pt-4 pb-3">
      <div className="h-14 w-14 animate-pulse rounded-2xl bg-white/[0.07]" />
      <div className="h-3 w-16 animate-pulse rounded bg-white/[0.07]" />
    </div>
  );
}

export default function Home() {
  const { user, isAdmin, logout } = useAuth();

  const [apps, setApps] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [editingApp, setEditingApp] = useState(null);
  const [showAppModal, setShowAppModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const [appList, categoryList] = await Promise.all([api.apps.list(), api.categories.list()]);
      setApps(appList);
      setCategories(categoryList);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openApp = (app) => window.open(app.url, "_blank", "noopener,noreferrer");

  /* ---------------------------------------------------------- grouping ---- */

  const matches = useCallback(
    (app) => {
      if (!search) return true;
      const needle = search.toLowerCase();
      return [app.name, app.category_name, app.description, app.url]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(needle));
    },
    [search],
  );

  const visibleApps = useMemo(() => apps.filter(matches), [apps, matches]);

  const groups = useMemo(() => {
    const byKey = new Map();
    for (const category of categories) {
      byKey.set(category.id, { key: category.id, name: category.name, apps: [] });
    }
    byKey.set(UNCATEGORIZED, { key: UNCATEGORIZED, name: "General", apps: [] });

    for (const app of visibleApps) {
      const key = app.category_id && byKey.has(app.category_id) ? app.category_id : UNCATEGORIZED;
      byKey.get(key).apps.push(app);
    }
    for (const group of byKey.values()) group.apps.sort((a, b) => a.position - b.position);

    return byKey;
  }, [visibleApps, categories]);

  const orderedGroups = useMemo(() => categories.map((c) => groups.get(c.id)), [categories, groups]);
  const looseGroup = groups.get(UNCATEGORIZED);

  // Admins keep empty categories on screen so there is somewhere to drop cards.
  const showGroup = (group) => group.apps.length > 0 || (isAdmin && !search);

  /* ----------------------------------------------------- drag and drop ---- */

  const handleDragEnd = async (result) => {
    const { source, destination, type } = result;
    if (!destination || !isAdmin) return;

    if (type === "CATEGORY") {
      if (source.index === destination.index) return;

      const next = [...categories];
      const [moved] = next.splice(source.index, 1);
      next.splice(destination.index, 0, moved);
      setCategories(next.map((c, i) => ({ ...c, position: i })));

      try {
        await api.categories.reorder(next.map((c) => c.id));
      } catch (err) {
        toast.error(`Couldn't save the category order. ${err.message}`);
        load();
      }
      return;
    }

    const from = source.droppableId;
    const to = destination.droppableId;
    if (from === to && source.index === destination.index) return;

    const lists = new Map([...groups].map(([key, group]) => [key, [...group.apps]]));
    const [moved] = lists.get(from).splice(source.index, 1);
    lists.get(to).splice(destination.index, 0, moved);

    // Show the new arrangement immediately, then confirm with the server.
    setApps(
      [...lists].flatMap(([key, list]) =>
        list.map((app, index) => ({ ...app, category_id: keyToCategoryId(key), position: index })),
      ),
    );

    const touched = from === to ? [to] : [from, to];
    try {
      const updated = await api.apps.reorder(
        touched.map((key) => ({
          category_id: keyToCategoryId(key),
          ids: lists.get(key).map((a) => a.id),
        })),
      );
      setApps(updated);
    } catch (err) {
      toast.error(`Couldn't save the new order. ${err.message}`);
      load();
    }
  };

  /* --------------------------------------------------------- app edits ---- */

  const saveApp = async (form) => {
    try {
      if (editingApp) await api.apps.update(editingApp.id, form);
      else await api.apps.create(form);
      setEditingApp(null);
      await load();
      toast.success(editingApp ? "App updated" : "App added");
    } catch (err) {
      toast.error(err.message);
      throw err;
    }
  };

  const deleteApp = async (id) => {
    try {
      await api.apps.remove(id);
      await load();
      toast.success("App removed");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const editOnRightClick = (event, app) => {
    if (!isAdmin) return;
    event.preventDefault();
    setEditingApp(app);
    setShowAppModal(true);
  };

  /* ----------------------------------------------------------- render ---- */

  const renderCard = (app, index) => (
    <Draggable key={app.id} draggableId={app.id} index={index} isDragDisabled={!isAdmin || !!search}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          onContextMenu={(e) => editOnRightClick(e, app)}
        >
          <AppCard
            app={app}
            isDragging={snapshot.isDragging}
            dragHandleProps={provided.dragHandleProps}
            onOpen={openApp}
          />
        </div>
      )}
    </Draggable>
  );

  /**
   * Equal-width cards in a wrapping row read as a grid, and keep
   * @hello-pangea/dnd on the flex layout it handles predictably.
   */
  const renderGroupBody = (group) => (
    <Droppable droppableId={group.key} direction="horizontal" type="APP" isDropDisabled={!!search}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={`flex flex-wrap gap-1 rounded-2xl transition-colors ${
            isAdmin ? "min-h-[122px]" : ""
          } ${snapshot.isDraggingOver ? "bg-white/[0.04] ring-1 ring-inset ring-white/10" : ""}`}
        >
          {group.apps.map(renderCard)}
          {provided.placeholder}
          {group.apps.length === 0 && isAdmin && !snapshot.isDraggingOver && (
            <p className="self-center px-3 text-xs text-white/25">Drag apps here</p>
          )}
        </div>
      )}
    </Droppable>
  );

  const groupHeading = (group, handleProps) => (
    <div className="mb-3 flex items-baseline gap-3 px-1">
      <h2
        className={`text-[11px] font-semibold uppercase tracking-[0.14em] text-white/70 ${
          isAdmin && !search ? "cursor-grab active:cursor-grabbing" : ""
        }`}
        {...handleProps}
      >
        {group.name}
      </h2>
      <span className="text-[11px] tabular-nums text-white/30">{group.apps.length}</span>
      <span className="h-px flex-1 bg-gradient-to-r from-white/10 to-transparent" />
    </div>
  );

  const header = (
    <header className="sticky top-0 z-20 border-b border-white/[0.08] bg-[#0b1020]/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-3 px-4 py-3 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] sm:px-6 sm:py-3.5">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/10">
            <LayoutGrid className="h-4 w-4 text-white" />
          </span>
          <h1 className="text-base font-semibold tracking-tight text-white">App Hub</h1>
        </div>

        <div className="order-last w-full min-w-0 sm:order-none sm:ml-2 sm:w-auto sm:flex-1 sm:max-w-md">
          <SearchBar value={search} onChange={setSearch} />
        </div>

        <div className="ml-auto flex items-center gap-1">
          {isAdmin && (
            <>
              <button
                onClick={() => {
                  setEditingApp(null);
                  setShowAppModal(true);
                }}
                className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-sm font-medium text-white ring-1 ring-inset ring-white/15 coarse:py-2.5 transition hover:bg-white/[0.18]"
              >
                <Plus className="h-4 w-4" /> Add app
              </button>
              <button
                onClick={() => setShowCategoryModal(true)}
                title="Manage categories"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-white/55 transition hover:bg-white/10 hover:text-white coarse:h-11 coarse:w-11"
              >
                <FolderCog className="h-4 w-4" />
              </button>
              <Link
                to="/admin"
                title="Admin"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-white/55 transition hover:bg-white/10 hover:text-white coarse:h-11 coarse:w-11"
              >
                <Settings className="h-4 w-4" />
              </Link>
            </>
          )}

          <div className="ml-1 flex items-center gap-2 border-l border-white/10 pl-3">
            <div className="hidden text-right leading-tight sm:block">
              <p className="text-[13px] font-medium text-white">{user?.full_name}</p>
              <p className="text-[11px] text-white/40">{user?.email}</p>
            </div>
            <button
              onClick={logout}
              title="Sign out"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-white/55 transition hover:bg-white/10 hover:text-white coarse:h-11 coarse:w-11"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className="app-surface min-h-dvh">
        {header}
        <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
          <div className="mb-3 h-3 w-24 animate-pulse rounded bg-white/[0.07]" />
          <div className="flex flex-wrap gap-1">
            {Array.from({ length: 8 }, (_, i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app-surface min-h-dvh">
      {header}

      <main className="mx-auto max-w-6xl px-4 pt-6 pb-[max(5rem,env(safe-area-inset-bottom))] pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] sm:px-6 sm:pt-8">
        {search ? (
          <section>
            <p className="mb-4 px-1 text-sm text-white/45">
              {visibleApps.length} {visibleApps.length === 1 ? "result" : "results"} for “{search}”
            </p>
            <div className="flex flex-wrap gap-1">
              {visibleApps.map((app) => (
                <div key={app.id} onContextMenu={(e) => editOnRightClick(e, app)}>
                  <AppCard app={app} onOpen={openApp} />
                </div>
              ))}
            </div>
          </section>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="categories" type="CATEGORY" direction="vertical">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {orderedGroups.map((group, index) =>
                    !showGroup(group) ? null : (
                      <Draggable
                        key={group.key}
                        draggableId={`category-${group.key}`}
                        index={index}
                        isDragDisabled={!isAdmin}
                      >
                        {(dragProvided, snapshot) => (
                          <section
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            className={`mb-9 ${snapshot.isDragging ? "rounded-2xl bg-white/[0.04] p-2 ring-1 ring-white/10" : ""}`}
                          >
                            {groupHeading(group, dragProvided.dragHandleProps)}
                            {renderGroupBody(group)}
                          </section>
                        )}
                      </Draggable>
                    ),
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>

            {showGroup(looseGroup) && (
              <section className="mb-9">
                {groupHeading(looseGroup)}
                {renderGroupBody(looseGroup)}
              </section>
            )}
          </DragDropContext>
        )}

        {visibleApps.length === 0 && (
          <div className="mx-auto max-w-sm py-24 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white/[0.06] ring-1 ring-white/10">
              <LayoutGrid className="h-5 w-5 text-white/40" />
            </div>
            <p className="mb-1 text-white/80">{search ? "Nothing matches that" : "No apps yet"}</p>
            <p className="text-sm text-white/40">
              {search
                ? "Try a shorter search, or a different word."
                : isAdmin
                  ? "Add your first app to get started."
                  : "Nothing has been shared with your account yet."}
            </p>
          </div>
        )}
      </main>

      {isAdmin && (
        <>
          <AppFormModal
            open={showAppModal}
            onClose={() => {
              setShowAppModal(false);
              setEditingApp(null);
            }}
            onSave={saveApp}
            onDelete={deleteApp}
            app={editingApp}
            categories={categories}
          />
          <CategoryManagerModal
            open={showCategoryModal}
            onClose={() => setShowCategoryModal(false)}
            categories={categories}
            onChanged={load}
          />
        </>
      )}
    </div>
  );
}
